import * as React from "react";
import {Col, ControlLabel, FormControl, FormGroup} from "react-bootstrap";
import {Button, Checkbox, Form, InputGroup, Radio} from "react-bootstrap/lib";
import {defineMessages, FormattedMessage, InjectedIntlProps, injectIntl,} from "react-intl";
import {AccessRule, SpatialRange} from "../model";
import {Role} from "../user/model";
import {
  convertAccessRulesToSecurityFormAccessRules,
  getGeometryStringFromFeature, getFeatureFromGeometryString,
  OverlappingShapesType, PermissionsType,
  SecurityFormAccessRule, SpatialAreaAcceptType
} from "./model";
import {LcdIcon} from "../common/ui/icon/LcdIcon";
import {Feature} from "@luciad/ria/model/feature/Feature.js";
import {DataCategory} from "../data/model";

export interface SecurityFormProps {
  accessRules: AccessRule[];
  isUserManagementActive: boolean;
  allRoles: Role[];
  dataType: string;
  dataCategories?: string[];
  onDrawSpatialArea: () => void;
  onEditSpatialArea: (feature: Feature) => void;
  onFileSpatialArea: () => void;
  spatialAreaFeature?: Feature;
  addCrawlDefaultRoles?: boolean;
  unmappedRoles: AccessRule[];
}

interface SecurityFormState {
  accessRules: SecurityFormAccessRule[];
  selectedAccessRule: SecurityFormAccessRule;
  newRoleName: string;
  deletedAccessRules: SecurityFormAccessRule[];
  isUserManagementActive: boolean;
  unmappedRoles: SecurityFormAccessRule[];
  loadFromSettingPage: boolean;
}

const MESSAGES = defineMessages({
  newRolePlaceholder: {
    id: "studio.data-security-dialog.new-role-name-placeholder",
    defaultMessage: "Enter role name",
  },
});

const ROLE_ADMIN = "ADMIN";

export const ITEM_HAS_NO_RIGHTS = "item-has-no-rights";

export class SecurityFormComponent extends React.Component<SecurityFormProps & InjectedIntlProps, SecurityFormState> {

  constructor(props) {
    super(props);
    this.state = this.getDefaultState();
  }

  getDefaultState(): SecurityFormState {
    return {
      accessRules: [],
      selectedAccessRule: null,
      newRoleName: "",
      deletedAccessRules: [],
      isUserManagementActive: true,
      unmappedRoles: [],
      loadFromSettingPage: false,
    };
  }

  componentDidUpdate(prevProps: SecurityFormProps) {
    if (this.props.spatialAreaFeature && this.props.spatialAreaFeature !== prevProps.spatialAreaFeature) {
      const {selectedAccessRule} = this.state;
      selectedAccessRule.spatialRange.area = getGeometryStringFromFeature(this.props.spatialAreaFeature);
      this.setState({selectedAccessRule});
    }
  }

  componentDidMount() {
    const loadFromSettingPage = this.props.dataType === "none";
    const isUserManagementActive = this.props.isUserManagementActive;
    const roles = this.props.allRoles;
    const accessRules = convertAccessRulesToSecurityFormAccessRules(this.props.accessRules);
    const unmappedRolesReformatted = convertAccessRulesToSecurityFormAccessRules(this.props.unmappedRoles);

    const selectedAccessRule = accessRules.length > 0 ? accessRules[0] : null;
    let mergedRoles: SecurityFormAccessRule[];
    if (!loadFromSettingPage) {
      const unmappedToMerge: SecurityFormAccessRule[] = roles.map((pp): SecurityFormAccessRule => {
        if (unmappedRolesReformatted.length > 0 && unmappedRolesReformatted.find(f => f?.role === pp.role) ===
            undefined) {
          return {
            role: pp.role,
            permissions: [PermissionsType.READ],
            isNew: false,
            scaleRange: null,
            scaleRangeEnabled: false,
            spatialRange: null,
            spatialRangeEnabled: false,
            applyToChildren: false
          } as SecurityFormAccessRule;
        }
      }).filter((newP => newP !== null && newP !== undefined));
      mergedRoles = unmappedRolesReformatted !== undefined && unmappedToMerge !== undefined
                    ? unmappedRolesReformatted.concat(unmappedToMerge) : [];
    } else {
      mergedRoles = unmappedRolesReformatted;
    }
    //Set the newRoleName to an unmapped role so the plus sign will enable
    const availableRoles = mergedRoles.filter((role) => {
      return accessRules.every(pp => pp.role !== role.role && selectedAccessRule.role !== role.role);
    });

    this.setState({
      accessRules,
      unmappedRoles: mergedRoles,
      selectedAccessRule,
      isUserManagementActive,
      newRoleName: availableRoles[0]?.role || "",
      loadFromSettingPage,
    });
  }

  getResult = () => {
    const {accessRules, deletedAccessRules, loadFromSettingPage} = this.state;
    let accessRulesToSave;
    let deletedPermissionsToSave;
    if (loadFromSettingPage) {
      //this writes back to the configuration/crawlAccessRules so make sure it includes the default roles with additions.
      accessRulesToSave = accessRules;
      deletedPermissionsToSave = deletedAccessRules.filter(p => !this.ciEquals(p.role, ROLE_ADMIN)).map(p => {
        const {scaleRangeEnabled, scaleRange, spatialRangeEnabled, spatialRange, ...rest} = p;
        return {
          ...rest,
          scaleRange: scaleRangeEnabled ? scaleRange : undefined,
          spatialRange: spatialRangeEnabled ? spatialRange : undefined,
        };
      });
    } else {
      accessRulesToSave = accessRules.filter(p => !this.ciEquals(p.role, ITEM_HAS_NO_RIGHTS)).map(p => {
        const {...rest} = p;
        return {
          ...rest,
        };
      });
      deletedPermissionsToSave = deletedAccessRules.filter(
          p => !this.ciEquals(p.role, ROLE_ADMIN) && !this.ciEquals(p.role, ITEM_HAS_NO_RIGHTS)).map(p => {
        const {...rest} = p;
        return {
          ...rest,
        };
      });
    }

    return {
      deletedPermissionsToSave,
      permissionsToSave: accessRulesToSave,
    };
  }

  private onRoleChange(roleName: string) {
    this.setState(prevState => ({selectedAccessRule: prevState.accessRules.find(p => p.role === roleName)}));
  }

  private addRole() {
    // Set the newRoleName to an unmapped role so the plus sign will enable
    const availableRoles = this.state.unmappedRoles.filter((role) => {
      return this.state.accessRules.every(pp => pp.role !== role.role) && this.state.newRoleName.trim() !== role.role;
    });
    const newRoleName = availableRoles[0]?.role || "";
    const newAccessRule: SecurityFormAccessRule = {
      isNew: true,
      role: this.state.newRoleName.trim(),
      permissions: [PermissionsType.READ],
      scaleRange: null,
      scaleRangeEnabled: false,
      spatialRange: null,
      spatialRangeEnabled: false,
      applyToChildren: false
    } as SecurityFormAccessRule;
    this.setState(prevState => ({
      selectedAccessRule: newAccessRule,
      accessRules: prevState.accessRules.concat(newAccessRule),
      newRoleName,
    }));
  }

  private permissionChange(permission: PermissionsType) {
    const {selectedAccessRule} = this.state;
    const index = selectedAccessRule.permissions.indexOf(permission);
    if (index >= 0) {
      selectedAccessRule.permissions.splice(index, 1);
    } else {
      selectedAccessRule.permissions.push(permission);
    }
    this.setState({selectedAccessRule});
  }

  private newRoleNameChange(newRoleName: string) {
    if (newRoleName) {
      newRoleName = newRoleName.trim();
    }
    this.setState({newRoleName});
  }

  private removeSelectedRole() {
    this.setState(prevState => {
      let {selectedAccessRule, accessRules} = prevState;
      const {deletedAccessRules} = prevState;

      if (!selectedAccessRule.isNew) {
        deletedAccessRules.push(selectedAccessRule);
      }
      accessRules = accessRules.filter(p => p !== selectedAccessRule);
      selectedAccessRule = accessRules.length > 0 ? accessRules[0] : null;
      return {selectedAccessRule, accessRules, deletedAccessRules};
    });
  }

  private toggleApplyToChildren() {
    const {selectedAccessRule} = this.state;
    selectedAccessRule.applyToChildren = !selectedAccessRule.applyToChildren;
    this.setState({selectedAccessRule});
  }

  private toggleScaleRange() {
    const {selectedAccessRule} = this.state;
    selectedAccessRule.scaleRangeEnabled = !selectedAccessRule.scaleRangeEnabled;
    if (selectedAccessRule.scaleRangeEnabled && !selectedAccessRule.scaleRange) {
      selectedAccessRule.scaleRange = {
        minScaleDenominator: 0,
        maxScaleDenominator: 250000000,
      };
    }
    this.setState({selectedAccessRule});
  }

  private toggleSpatialArea() {
    const {selectedAccessRule} = this.state;
    selectedAccessRule.spatialRangeEnabled = !selectedAccessRule.spatialRangeEnabled;
    if (selectedAccessRule.spatialRangeEnabled && !selectedAccessRule.spatialRange) {
      selectedAccessRule.spatialRange = {
        area: null,
        accept: SpatialAreaAcceptType.INSIDE,
        spatialReference: "EPSG:4326",
        overlappingShapes: OverlappingShapesType.EXCLUDE,
      } as SpatialRange;
    }
    this.setState({selectedAccessRule});
  }

  private updateOverlappingShapes(newType: OverlappingShapesType) {
    const {selectedAccessRule} = this.state;
    selectedAccessRule.spatialRange.overlappingShapes = newType;
    this.setState({selectedAccessRule});
  }

  private setSpatialAreaAccept(newValue: SpatialAreaAcceptType) {
    const {selectedAccessRule} = this.state;
    selectedAccessRule.spatialRange.accept = newValue;
    this.setState({selectedAccessRule});
  }

  private setScaleRangeMinScaleDenominator(value: string) {
    const {selectedAccessRule} = this.state;
    const minScaleDenominatorValue = parseInt(value, 10);
    if (Number.isInteger(minScaleDenominatorValue)) {
      selectedAccessRule.scaleRange.minScaleDenominator = minScaleDenominatorValue;
    } else {
      delete selectedAccessRule.scaleRange.minScaleDenominator;
    }

    this.setState({selectedAccessRule});
  }

  private setScaleRangeMaxScaleDenominator(value: string) {
    const {selectedAccessRule} = this.state;
    const maxScaleDenominatorValue = parseInt(value, 10);
    if (Number.isInteger(maxScaleDenominatorValue)) {
      selectedAccessRule.scaleRange.maxScaleDenominator = maxScaleDenominatorValue;
    } else {
      delete selectedAccessRule.scaleRange.maxScaleDenominator;
    }

    this.setState({selectedAccessRule});
  }

  //case-insensitive compare since lowercase/uppercase checks don't work for all languages
  //https://medium.com/nerd-for-tech/basics-of-javascript-string-localecompare-method-b2aa50207706
  ciEquals = (a, b) => {
    return typeof a === 'string' && typeof b === 'string'
           ? a.localeCompare(b, undefined, {sensitivity: 'accent'}) === 0
           : a === b;
  }

  render() {
    const {selectedAccessRule, accessRules, newRoleName} = this.state;
    const {intl, onDrawSpatialArea, onEditSpatialArea, onFileSpatialArea, dataType, dataCategories} = this.props;
    // isReadOnly flag determines whether minus sign is enabled or not. Do not allow ADMIN role to be removed and only enable
    // if the selected role is a mapped role.
    const isReadOnly = this.ciEquals(selectedAccessRule?.role, ROLE_ADMIN) &&
                       this.state.accessRules.some(p => p.role === selectedAccessRule?.role);
    const isFolder = dataType === "folder";
    const launchedFromSettingsPage: boolean = dataType === "none";
    const isNewRoleValid = newRoleName.length > 0 &&
                           !this.state.accessRules.some(p => p.role === newRoleName);

    if (!selectedAccessRule) {
      // this should only ever happen during form initialization, this dialog should never be rendered with no permissions
      return null;
    }

    const sortedAvailableRules = this.getAvailableRoles().sort(
        (a: SecurityFormAccessRule, b: SecurityFormAccessRule) => {
          return a.role.localeCompare(b.role);
        }
    );
    accessRules.sort((a: SecurityFormAccessRule, b: SecurityFormAccessRule) => {
      return a.role.localeCompare(b.role);
        }
    );

    return (
        <div data-cy="security-form">
          <Form horizontal>
            <FormGroup controlId="newRole">
              <Col sm={3}>
                <ControlLabel>
                  <FormattedMessage
                      id="studio.data-security-dialog.new-role"
                      defaultMessage="Add role"
                  />
                </ControlLabel>
              </Col>

              <Col sm={9}>
                <InputGroup>
                  {this.state.isUserManagementActive && <FormControl
                      componentClass="select"
                      value={newRoleName}
                      onChange={e => {
                        this.newRoleNameChange((e.target as any).value);
                      }}
                  >
                    {sortedAvailableRules.map(p =>
                        <option key={p.role} value={p.role}>{p.role}</option>
                    )}
                  </FormControl>}

                  {!this.state.isUserManagementActive && <FormControl
                      type="text"
                      placeholder={intl.formatMessage(MESSAGES.newRolePlaceholder)}
                      value={newRoleName}
                      onChange={e => {
                        e.preventDefault();
                        this.newRoleNameChange((e.target as any).value);
                      }}
                  />}

                  <InputGroup.Addon className="input-group-no-bg-btn">
                    <Button data-cy="add-role" onClick={_ => this.addRole()} disabled={!isNewRoleValid}>
                      <LcdIcon icon="plus"/>
                    </Button>
                  </InputGroup.Addon>
                </InputGroup>
              </Col>
            </FormGroup>

            <FormGroup controlId="role">
              <Col sm={3}>
                <ControlLabel>
                  <FormattedMessage
                      id="studio.data-security-dialog.role"
                      defaultMessage="Role"
                  />
                </ControlLabel>
              </Col>
              <Col sm={9}>
                <InputGroup>
                  <FormControl componentClass="select"
                               data-cy="user-or-role-select-list"
                               value={selectedAccessRule.role}
                               onChange={(event) => {
                                 event.preventDefault();
                                 this.onRoleChange((event.target as any).value);
                               }}>
                    {accessRules.map((p) => {
                      return <option value={p.role} key={p.role}>{p.role}</option>;
                    })}
                  </FormControl>
                  <InputGroup.Addon className="input-group-no-bg-btn">
                    <Button bsStyle="danger" onClick={_ => this.removeSelectedRole()} disabled={isReadOnly}>
                      <LcdIcon icon="delete-circle"/>
                    </Button>
                  </InputGroup.Addon>
                </InputGroup>
              </Col>
            </FormGroup>
            <FormGroup>
              <Col sm={12}>
                <ControlLabel>
                  <FormattedMessage id="studio.data-security-dialog.label.permissions" defaultMessage="Permissions"/>
                </ControlLabel>
              </Col>
              <Col sm={12}>
                <Checkbox inline checked={selectedAccessRule.permissions.includes(PermissionsType.READ)}
                          onChange={_ => this.permissionChange(PermissionsType.READ)}
                          disabled={isReadOnly}>
                  <FormattedMessage id="studio.data-security-dialog.label.permissions.read" defaultMessage="Read"/>
                </Checkbox>
                <Checkbox inline checked={selectedAccessRule.permissions.includes(PermissionsType.UPDATE)}
                          onChange={_ => this.permissionChange(PermissionsType.UPDATE)}
                          disabled={isReadOnly}>
                  <FormattedMessage id="studio.data-security-dialog.label.permissions.update" defaultMessage="Update"/>
                </Checkbox>
                <Checkbox inline checked={selectedAccessRule.permissions.includes(PermissionsType.DELETE)}
                          onChange={_ => this.permissionChange(PermissionsType.DELETE)}
                          disabled={isReadOnly}>
                  <FormattedMessage id="studio.data-security-dialog.label.permissions.delete" defaultMessage="Delete"/>
                </Checkbox>
                <Checkbox inline checked={selectedAccessRule.permissions.includes(PermissionsType.MANAGE)}
                          onChange={_ => this.permissionChange(PermissionsType.MANAGE)}
                          disabled={isReadOnly}>
                  <FormattedMessage id="studio.data-security-dialog.label.permissions.manage" defaultMessage="Manage"/>
                </Checkbox>
              </Col>
            </FormGroup>
            {(!dataCategories || dataCategories.some(c => c === DataCategory.RASTER || c === DataCategory.VECTOR)) &&
            <FormGroup>
              <FormGroup>
                <Col sm={12}>
                  <ControlLabel>
                    <FormattedMessage id="studio.data-security-dialog.label.scale-range" defaultMessage="Scale Range"/>
                  </ControlLabel>
                </Col>
                <Col sm={12}>
                  <div>
                    <Checkbox inline checked={selectedAccessRule.scaleRangeEnabled}
                              onChange={_ => this.toggleScaleRange()}
                              disabled={isReadOnly}>
                      <FormattedMessage id="studio.data-security-dialog.label.enable-scale-range"
                                        defaultMessage="Authorized Scale Range"/>
                    </Checkbox>
                  </div>
                </Col>
              </FormGroup>
              <div hidden={!selectedAccessRule.scaleRangeEnabled}>
                <Col sm={12}>
                  <FormGroup controlId="minScaleDenominator">
                    <Col sm={3}>
                      <ControlLabel>
                        <FormattedMessage
                            id="studio.data-security-dialog.scale-range.min-scale-denominator"
                            defaultMessage="Min scale denominator:"
                        />
                      </ControlLabel>
                    </Col>
                    <Col sm={6}>
                      <FormControl type="text"
                                   disabled={!selectedAccessRule.scaleRangeEnabled}
                                   value={Number.isInteger(selectedAccessRule.scaleRange?.minScaleDenominator)
                                          ? selectedAccessRule.scaleRange?.minScaleDenominator : ""}
                                   onChange={e => this.setScaleRangeMinScaleDenominator((e.target as any).value)}>
                      </FormControl>
                    </Col>
                  </FormGroup>
                  <FormGroup controlId="maxScaleDenominator">
                    <Col sm={3}>
                      <ControlLabel>
                        <FormattedMessage
                            id="studio.data-security-dialog.scale-range.max-scale-denominator"
                            defaultMessage="Max scale denominator:"
                        />
                      </ControlLabel>
                    </Col>
                    <Col sm={6}>
                      <FormControl type="text"
                                   disabled={!selectedAccessRule.scaleRangeEnabled}
                                   value={Number.isInteger(selectedAccessRule.scaleRange?.maxScaleDenominator)
                                          ? selectedAccessRule.scaleRange?.maxScaleDenominator : ""}
                                   onChange={e => this.setScaleRangeMaxScaleDenominator((e.target as any).value)}>
                      </FormControl>
                    </Col>
                  </FormGroup>
                </Col>
              </div>
            </FormGroup>
            }
            {(!dataCategories || dataCategories.some(c => c === DataCategory.RASTER || c === DataCategory.VECTOR)) &&
             <FormGroup>
              <FormGroup>
                <Col sm={12}>
                  <ControlLabel>
                    <FormattedMessage id="studio.data-security-dialog.label.spatial-area"
                                      defaultMessage="Spatial Area"/>
                  </ControlLabel>
                </Col>
                <Col sm={12}>
                  <div>
                    <Checkbox data-cy="authorize-spatial-area-checkbox" inline
                              checked={selectedAccessRule.spatialRangeEnabled} onChange={_ => this.toggleSpatialArea()}
                              disabled={isReadOnly}>
                      <FormattedMessage id="studio.data-security-dialog.label.enable-spatial-area"
                                        defaultMessage="Authorized Spatial Area"/>
                    </Checkbox>
                  </div>
                </Col>
              </FormGroup>
              <div hidden={!selectedAccessRule.spatialRangeEnabled}>
                <Col sm={12}>
                  {(!dataCategories || dataCategories.includes(DataCategory.VECTOR)) &&
                   <FormGroup controlId="spatialAreaOverlappingShapes">
                    <Col sm={3}>
                      <ControlLabel>
                        <FormattedMessage id="studio.data-security-dialog.label.spatial-area-overlapping-shapes"
                                          defaultMessage="Overlapping Shapes:"/>
                      </ControlLabel>
                    </Col>
                    <Col sm={6}>
                      <Radio name="spatialAreaOverlappingShapes"
                             inline
                             disabled={!selectedAccessRule.spatialRangeEnabled}
                             checked={selectedAccessRule?.spatialRange?.overlappingShapes ===
                                      OverlappingShapesType.INCLUDE}
                             onChange={_ => {
                               this.updateOverlappingShapes(OverlappingShapesType.INCLUDE)
                             }}>
                        <FormattedMessage id="studio.data-security-dialog.label.spatial-area-overlapping-shapes-include"
                                          defaultMessage="Include"/>
                      </Radio>
                      <Radio name="spatialAreaOverlappingShapes"
                             inline
                             disabled={!selectedAccessRule.spatialRangeEnabled}
                             checked={selectedAccessRule?.spatialRange?.overlappingShapes ===
                                      OverlappingShapesType.EXCLUDE}
                             onChange={_ => {
                               this.updateOverlappingShapes(OverlappingShapesType.EXCLUDE)
                             }}>
                        <FormattedMessage id="studio.data-security-dialog.label.spatial-area-overlapping-shapes-exclude"
                                          defaultMessage="Exclude"/>
                      </Radio>
                      <Radio name="spatialAreaOverlappingShapes"
                             inline
                             disabled={!selectedAccessRule.spatialRangeEnabled}
                             checked={selectedAccessRule?.spatialRange?.overlappingShapes ===
                                      OverlappingShapesType.CLIP}
                             onChange={_ => {
                               this.updateOverlappingShapes(OverlappingShapesType.CLIP)
                             }}>
                        <FormattedMessage id="studio.data-security-dialog.label.spatial-area-overlapping-shapes-clip"
                                          defaultMessage="Clip"/>
                      </Radio>
                    </Col>
                  </FormGroup>
                  }
                  <FormGroup controlId="spatialAreaInclusive">
                    <Col sm={3}>
                      <ControlLabel>
                        <FormattedMessage id="studio.data-security-dialog.label.spatial-area-inclusive"
                                          defaultMessage="Spatial Area:"/>
                      </ControlLabel>
                    </Col>
                    <Col sm={6}>
                      <Radio name="spatialAreaInclusive"
                             inline
                             disabled={!selectedAccessRule.spatialRangeEnabled}
                             checked={selectedAccessRule?.spatialRange?.accept === SpatialAreaAcceptType.INSIDE}
                             onChange={_ => this.setSpatialAreaAccept(SpatialAreaAcceptType.INSIDE)}>
                        <FormattedMessage id="studio.data-security-dialog.label.spatial-area-inside"
                                          defaultMessage="Inside"/>
                      </Radio>
                      <Radio name="spatialAreaInclusive"
                             inline
                             disabled={!selectedAccessRule.spatialRangeEnabled}
                             checked={selectedAccessRule?.spatialRange?.accept === SpatialAreaAcceptType.OUTSIDE}
                             onChange={_ => this.setSpatialAreaAccept(SpatialAreaAcceptType.OUTSIDE)}>
                        <FormattedMessage id="studio.data-security-dialog.label.spatial-area-ouside"
                                          defaultMessage="Outside"/>
                      </Radio>
                    </Col>
                  </FormGroup>
                  <FormGroup controlId="spatialAreaDraw">
                    <Col sm={3}>
                      <ControlLabel>
                        <FormattedMessage id="studio.data-security-dialog.label.spatial-area-define"
                                          defaultMessage="Define:"/>
                      </ControlLabel>
                    </Col>
                    <Col sm={8}>
                      <Button data-cy="draw-area-on-map-btn"
                              disabled={!selectedAccessRule.spatialRangeEnabled}
                              onClick={_ => onDrawSpatialArea()}>
                        <FormattedMessage
                            id="studio.data-security-dialog.label.spatial-area-draw"
                            defaultMessage="Draw area on map"
                        />
                      </Button>
                      &nbsp;
                      <Button data-cy="use-existing-area-btn"
                              disabled={!selectedAccessRule.spatialRangeEnabled}
                              onClick={_ => onFileSpatialArea()}>
                        <FormattedMessage
                            id="studio.data-security-dialog.label.spatial-area-existing"
                            defaultMessage="Use local file"
                        />
                      </Button>
                    </Col>
                  </FormGroup>
                  <FormGroup controlId="spatialAreaDraw">
                    <Col sm={3}>
                      <ControlLabel>
                        <FormattedMessage id="studio.data-security-dialog.label.spatial-area-review"
                                          defaultMessage="Review:"/>
                      </ControlLabel>
                    </Col>
                    <Col sm={8}>
                      <Button data-cy="review-existing-area-on-map-btn"
                              disabled={!selectedAccessRule.spatialRangeEnabled ||
                                        selectedAccessRule.spatialRange?.area === null}
                              onClick={_ => onEditSpatialArea(
                                  getFeatureFromGeometryString(selectedAccessRule.spatialRange.area))}>
                        <FormattedMessage
                            id="studio.data-security-dialog.label.spatial-area-review"
                            defaultMessage="Review or Adjust"
                        />
                      </Button>
                    </Col>
                  </FormGroup>
                </Col>
              </div>
            </FormGroup>
            }
            {!launchedFromSettingsPage && isFolder &&
             <Checkbox inline checked={selectedAccessRule.applyToChildren} onChange={_ => this.toggleApplyToChildren()}
                       disabled={isReadOnly || !isFolder}>
               <FormattedMessage id="studio.data-security-dialog.label.permissions.apply.to.children"
                                 defaultMessage="Apply To Children"/>
             </Checkbox>}
          </Form>
        </div>
    );
  }

  private getAvailableRoles() {
    return this.state.unmappedRoles.filter((p) => {
      return this.state.accessRules.every(pp => pp.role !== p.role);
    });
  }
}

export const SecurityForm = injectIntl(SecurityFormComponent, {withRef: true})